<script setup lang="ts">
import { computed } from 'vue'

import { outcomePresentation } from '../../service/insightPresentation'

import { useI18n } from '@/shared/i18n'
import { Badge } from '@/shared/ui/badge'

// One exclusive utility pair per outcome: the accent is keyed on `outcome_category`, not on a
// semantic tone, so the Badge steps back to `tone="plain"` and this table owns the colour. Two
// competing `bg-*`/`text-*` utilities on one element would be resolved by the emitted sheet's
// order rather than by the class attribute, which is why the primitive paints nothing here.
const OUTCOME_TONE_CLASS: Record<string, string> = {
  revenue_gain: 'bg-[var(--ui-color-insight-revenue-soft)] text-[var(--ui-color-insight-revenue)]',
  roas_uplift: 'bg-[var(--ui-color-insight-roas-soft)] text-[var(--ui-color-insight-roas)]',
  cost_savings: 'bg-[var(--ui-color-insight-savings-soft)] text-[var(--ui-color-insight-savings)]',
  lost_revenue: 'bg-[var(--ui-color-insight-risk-soft)] text-[var(--ui-color-insight-risk)]',
  planning: 'bg-[var(--ui-color-insight-planning-soft)] text-[var(--ui-color-insight-planning)]',
}
const NEUTRAL_TONE_CLASS =
  'bg-[var(--ui-color-insight-neutral-soft)] text-[var(--ui-color-insight-neutral)]'

const props = withDefaults(defineProps<{ value: unknown; strong?: boolean }>(), { strong: false })
const { t } = useI18n()
const presentation = computed(() => outcomePresentation(props.value))
const toneClass = computed(() => OUTCOME_TONE_CLASS[presentation.value.key] ?? NEUTRAL_TONE_CLASS)
</script>

<template>
  <Badge
    class="insight-outcome-badge"
    :class="[`insight-outcome-badge--${presentation.key}`, toneClass]"
    tone="plain"
    :strong="strong"
    >{{ t(presentation.labelKey) }}</Badge
  >
</template>
